import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { dateFormat, to12HourFormat } from "../utils/dateFormats";
import axiosInstance from "../utils/axios";
import "../styles/BookingSuccessPage.scss";

export default function EventTicketPage() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchEventTicket() {
      try {
        setLoading(true);
        // Fetch event ticket data from API - using similar pattern as movie ticket
        const { data } = await axiosInstance.get(
          `/news_and_events/event-ticket/verify/${id}`
        );
        setTicket(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching event ticket:", err);
        setError("Failed to load event ticket. Please check the booking ID.");
        setLoading(false);
      }
    }
    fetchEventTicket();
  }, [id]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading your ticket...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-page container py-5 d-flex justify-content-center">
        <div className="booking-card shadow p-4 bg-white">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) return <div>No event ticket data found</div>;

  return (
    <div className="booking-page container py-5 d-flex justify-content-center">
      <div className="booking-card shadow p-4 bg-white">

        {/* HEADER */}
        <div className="row mb-4">
          <div className="col-auto">
            <img
              src={ticket.event?.thumbnail || ticket.event?.image || require("../assets/images/logo.png")}
              alt="event poster"
              className="poster-img img-fluid rounded shadow-sm"
            />
          </div>

          <div className="col d-flex flex-column justify-content-center">
            <h1 className="h3 fw-bold mb-1">{ticket.message || "Event Ticket"}</h1>

            <p className="text-muted small mb-0">
              <strong>Booking ID:</strong> {ticket.booking_id || id}
            </p>
          </div>
        </div>

        {/* DETAILS */}
        <div className="details-list">
          <Detail label="Event" value={ticket.event?.heading || ticket.event?.name || ticket.event?.title} />
          <Detail label="Location" value={ticket.event?.venue || ticket.event?.location} />
          <Detail label="Event Date" value={`${dateFormat(ticket.event?.start_date).split(' ')[0]} - ${dateFormat(ticket.event?.end_date).split(' ')[0]}`} />
          <Detail label="Event Time" value={to12HourFormat(ticket.event?.time)} />
          <Detail label="Amount" value={`€${ticket.amount}`} />
        </div>

        {/* TICKETS */}
        <h2 className="h4 fw-semibold mt-4">Tickets</h2>

        <div className="table-responsive">
          <table className="table table-bordered table-hover table-sm">
            <thead className="table-light">
              <tr>
                <th>Section</th>
                <th>Quantity</th>
                <th>Price</th>
              </tr>
            </thead>

            <tbody>
              {ticket.sections && ticket.sections.map((section, index) => (
                <tr key={index}>
                  <td>{section.title}</td>
                  <td>{section.quantity || ticket.quantity}</td>
                  <td>€{section.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* FOOTER */}
        <p className="text-muted small mt-4">
          Show this confirmation at the event entrance.
        </p>

        <p className="text-center small text-muted">
          If you have questions, contact support.
        </p>

        <p className="text-center small text-secondary">© 2025 Getam</p>

        <div className="text-center mt-4">
          <button className="btn btn-dark px-4" onClick={() => window.print()}>
            Print Ticket
          </button>
        </div>

      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="detail-row d-flex border-bottom py-2">
      <span className="label fw-medium">{label}</span>
      <span className="value ms-3">{value}</span>
    </div>
  );
}
